import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'

const MAX_DOCUMENT_SIZE = 25 * 1024 * 1024
const ALLOWED_DOCUMENT_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp'
])

function getDocumentPath(value) {
  if (!value) return ''
  if (!/^https?:\/\//i.test(value)) return value

  const marker = '/storage/v1/object/public/documents/'
  const markerIndex = value.indexOf(marker)
  if (markerIndex === -1) return ''

  return decodeURIComponent(value.slice(markerIndex + marker.length).split('?')[0])
}

function sanitiseFileName(name) {
  return name.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(-120) || 'document'
}

const STATUS_BADGE = {
  pending: 'hv-badge-gray',
  'in progress': 'hv-badge-amber',
  delivered: 'hv-badge-green',
  approved: 'hv-badge-blue',
}

const STATUS_OPTIONS = ['pending', 'in progress', 'delivered', 'approved']

function formatDate(d) {
  if (!d) return null
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function cap(s) {
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export default function Deliveries() {
  const toast = useToast()
  const { orgId } = useUser()
  const [deliveries, setDeliveries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(null)
  const [saving, setSaving] = useState(null)
  const [expandedId, setExpandedId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [filterStatus, setFilterStatus] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetchDeliveries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function fetchDeliveries() {
    setError(null)
    setLoading(true)

    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      setError('Failed to load deliveries: no signed-in user found.')
      setLoading(false)
      return
    }

    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('id, role')

    if (profileError) {
      console.error('Failed to load profile:', profileError)
      setError(`Failed to load profile: ${profileError.message}`)
      setLoading(false)
      return
    }

    const currentProfile = profileData?.find(p => p.id === user.id)
    const isAdmin = currentProfile?.role === 'admin'

    let query = supabase
      .from('secure_deliveries')
      .select('*')
      .order('created_at', { ascending: false })

    if (!isAdmin) query = query.eq('assigned_to', user.id)

    const { data, error: fetchError } = await query

    if (fetchError) {
      console.error('Failed to load deliveries:', fetchError)
      setError(`Failed to load deliveries: ${fetchError.message}`)
      setLoading(false)
      return
    }

    setDeliveries(data || [])
    setLoading(false)
  }

  function openEdit(delivery) {
    setExpandedId(delivery.id)
    setEditForm({
      status: delivery.status || 'pending',
      delivery_date: delivery.delivery_date || '',
      delivered_by: delivery.delivered_by || '',
      url: delivery.url || '',
      notes: delivery.notes || '',
      file_url: delivery.file_url || ''
    })
  }

  async function handleFileUpload(e, deliveryId) {
    const file = e.target.files[0]
    if (!file) return

    if (!orgId) {
      toast.error('Your organisation could not be identified. Please sign in again.')
      e.target.value = ''
      return
    }

    if (!ALLOWED_DOCUMENT_TYPES.has(file.type)) {
      toast.error('Upload a PDF, Word document, JPEG, PNG or WebP file.')
      e.target.value = ''
      return
    }

    if (file.size > MAX_DOCUMENT_SIZE) {
      toast.error('Files must be 25 MB or smaller.')
      e.target.value = ''
      return
    }

    setUploading(deliveryId)

    const fileName = `${orgId}/${deliveryId}/${crypto.randomUUID()}-${sanitiseFileName(file.name)}`

    const { error } = await supabase.storage
      .from('documents')
      .upload(fileName, file)

    if (error) {
      toast.error('Upload failed: ' + error.message)
      setUploading(null)
      return
    }

    setEditForm(prev => ({ ...prev, file_url: fileName }))
    toast.success('File uploaded successfully')
    setUploading(null)
  }

  async function viewDocument(fileReference) {
    const path = getDocumentPath(fileReference)

    if (!path) {
      toast.error('This document reference is invalid.')
      return
    }

    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(path, 60)

    if (error || !data?.signedUrl) {
      toast.error(`Unable to open document: ${error?.message || 'Access denied'}`)
      return
    }

    window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
  }

  async function saveDelivery(id) {
    setSaving(id)

    const payload = {
      ...editForm,
      delivery_date: editForm.delivery_date || null,
      delivered_by: editForm.delivered_by || null,
      url: editForm.url || null,
      notes: editForm.notes || null,
      file_url: editForm.file_url || null
    }

    const { error } = await supabase
      .from('deliveries')
      .update(payload)
      .eq('id', id)

    setSaving(null)

    if (error) {
      console.error('Failed to save delivery:', error)
      toast.error(`Failed to save delivery: ${error.message}`)
      return
    }

    toast.success('Delivery updated')
    setExpandedId(null)
    fetchDeliveries()
  }

  const filtered = deliveries.filter(d => {
    const matchesStatus = filterStatus ? d.status === filterStatus : true

    const matchesSearch = search
      ? (d.rights?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (d.rights?.deals?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (d.rights?.deals?.clients?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (d.rights?.projects?.name || '').toLowerCase().includes(search.toLowerCase())
      : true

    return matchesStatus && matchesSearch
  })

  const completed = deliveries.filter(d => d.status === 'delivered' || d.status === 'approved').length

  return (
    <div className="hv-page">
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">Deliveries</h1>
          <p className="hv-page-subtitle">Track and update the status of all delivery items</p>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          fontSize: '13px',
          color: 'var(--hv-text-muted)'
        }}>
          <span>{deliveries.length} total</span>
          <span style={{ color: 'var(--hv-green)', fontWeight: '500' }}>
            {completed} completed
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div className="hv-search-wrap" style={{ margin: 0 }}>
          <span className="hv-search-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>

          <input
            className="hv-search"
            type="text"
            placeholder="Search by right, deal, project or client..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <select className="hv-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{cap(s)}</option>)}
        </select>
      </div>

      {error && (
        <div style={{
          background: 'var(--hv-red-bg)',
          border: '1px solid #fca5a5',
          borderRadius: 'var(--hv-radius)',
          padding: '14px 18px',
          color: 'var(--hv-red-text)',
          fontSize: '13.5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hv-text-muted)', fontSize: '13.5px', padding: '40px 0' }}>
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="hv-table-wrap">
          <div className="hv-empty">
            <div className="hv-empty-title">
              {search || filterStatus ? 'No deliveries match your filters' : 'No deliveries yet'}
            </div>
            <div className="hv-empty-text">
              {search || filterStatus
                ? 'Try adjusting your search or status filter.'
                : 'Deliveries will appear here once they are created for rights.'}
            </div>
          </div>
        </div>
      ) : (
        <div className="hv-table-wrap" style={{ overflow: 'hidden' }}>
          <div className="hv-table-count">
            {filtered.length} {filtered.length === 1 ? 'delivery' : 'deliveries'}
            {(search || filterStatus) ? ' found' : ''}
          </div>

          {filtered.map((delivery, i) => {
            const isExpanded = expandedId === delivery.id
            const isLast = i === filtered.length - 1

            return (
              <div
                key={delivery.id}
                style={{ borderBottom: isLast && !isExpanded ? 'none' : '1px solid var(--hv-border)' }}
              >
                <div
                  onClick={() => isExpanded ? setExpandedId(null) : openEdit(delivery)}
                  style={{
                    padding: '14px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    cursor: 'pointer',
                    transition: 'background 0.1s',
                    background: isExpanded ? 'var(--hv-bg)' : 'transparent',
                  }}
                  onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'var(--hv-surface-hover)' }}
                  onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent' }}
                >
                  <span className={`hv-badge ${STATUS_BADGE[delivery.status] || 'hv-badge-gray'}`} style={{ flexShrink: 0 }}>
                    {cap(delivery.status)}
                  </span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13.5px', fontWeight: '500', color: 'var(--hv-text-primary)', marginBottom: '2px' }}>
                      {delivery.rights?.name || '—'}
                    </div>

                    <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)' }}>
                      {delivery.rights?.deals?.name || 'No deal'}
                      {delivery.rights?.projects?.name && (
                        <> · {delivery.rights.projects.name}</>
                      )}
                      {delivery.rights?.deals?.clients?.name && (
                        <> · {delivery.rights.deals.clients.name}</>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    {delivery.url && (
                      <span title="Has proof URL" style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '20px',
                        background: 'var(--hv-blue-bg)',
                        color: 'var(--hv-blue-text)',
                        fontWeight: '500'
                      }}>
                        URL
                      </span>
                    )}

                    {delivery.file_url && (
                      <span title="Has proof file" style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '20px',
                        background: 'var(--hv-green-bg)',
                        color: 'var(--hv-green-text)',
                        fontWeight: '500'
                      }}>
                        File
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {delivery.delivery_date ? formatDate(delivery.delivery_date) : 'No date'}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--hv-accent)', flexShrink: 0 }}>
                    <span style={{ fontSize: '13px' }}>{isExpanded ? 'Close' : 'Edit'}</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{
                    padding: '20px 24px 24px',
                    background: 'var(--hv-bg)',
                    borderTop: '1px solid var(--hv-border)',
                    borderBottom: isLast ? 'none' : undefined
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '14px' }}>
                      <div>
                        <label className="hv-form-label">Status</label>
                        <select
                          className="hv-select"
                          value={editForm.status}
                          onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{cap(s)}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="hv-form-label">Delivery Date</label>
                        <input
                          className="hv-input"
                          type="date"
                          value={editForm.delivery_date}
                          onChange={e => setEditForm({ ...editForm, delivery_date: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="hv-form-label">Delivered By</label>
                        <input
                          className="hv-input"
                          type="text"
                          value={editForm.delivered_by}
                          onChange={e => setEditForm({ ...editForm, delivered_by: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="hv-form-label">Proof URL</label>
                        <input
                          className="hv-input"
                          type="text"
                          placeholder="https://"
                          value={editForm.url}
                          onChange={e => setEditForm({ ...editForm, url: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="hv-form-label">Notes</label>
                        <input
                          className="hv-input"
                          type="text"
                          value={editForm.notes}
                          onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="hv-form-label">Proof File</label>
                        <div style={{
                          border: '1px dashed var(--hv-border-strong)',
                          borderRadius: 'var(--hv-radius-sm)',
                          padding: '8px 12px',
                          background: 'var(--hv-surface)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                            onChange={e => handleFileUpload(e, delivery.id)}
                            style={{ fontSize: '12px', color: 'var(--hv-text-secondary)', flex: 1 }}
                          />

                          {uploading === delivery.id && (
                            <span style={{ fontSize: '12px', color: 'var(--hv-text-muted)', whiteSpace: 'nowrap' }}>
                              Uploading...
                            </span>
                          )}
                        </div>

                        {editForm.file_url && uploading !== delivery.id && (
                          <button
                            type="button"
                            onClick={() => viewDocument(editForm.file_url)}
                            style={{ border: 0, padding: 0, background: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--hv-accent)', display: 'block', marginTop: '6px' }}
                          >
                            View uploaded file
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        className="hv-btn hv-btn-primary"
                        onClick={() => saveDelivery(delivery.id)}
                        disabled={saving === delivery.id || uploading === delivery.id}
                      >
                        {saving === delivery.id ? 'Saving...' : 'Save Delivery'}
                      </button>

                      <button className="hv-btn hv-btn-secondary" onClick={() => setExpandedId(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
